/**
 * Callouts — a note on the plan: a cloud of text with a pointer to the thing
 * it is about.
 *
 * ANNOTATION, NOT BUILDING FABRIC. A callout is deliberately NOT a room
 * object: it has no dimensions, no ceiling, no footprint, and nothing should
 * ever collide with it, schedule it, take it off, or render it in 3D. So it
 * lives in its own top-level room array (`room.callouts`) rather than in
 * `room.objects`, where every one of those subsystems would pick it up.
 *
 * Two points, in WORLD space, because both have to survive pan and zoom:
 *
 *   anchor   what the note is about — the leader's arrowhead lands here
 *   cloud    where the text sits
 *
 * Everything else is in PIXELS, for the same reason the electrical symbols
 * are: text is a paper-space property. A note whose type shrank with the zoom
 * would be unreadable at the zoom where you can see enough plan to want one.
 *
 * PURE — no React, no canvas, no Firestore. Text measurement is injected,
 * because the only honest way to measure a string is to ask the context that
 * will draw it (same contract as `layoutMeasurements`).
 */

/** Type, leading and padding of the note body, all screen pixels. */
export const CALLOUT_FONT_PX = 12.5;
export const CALLOUT_LINE_HEIGHT_PX = 16;
export const CALLOUT_PADDING_PX = 11;

/** Where a line wraps. Wide enough for a short sentence, narrow enough to read. */
export const CALLOUT_MAX_WIDTH_PX = 190;

/** A cloud narrower than this looks like a smudge rather than a note. */
const CALLOUT_MIN_WIDTH_PX = 54;

/** Radius of the scallops that make the outline a cloud. */
export const CALLOUT_SCALLOP_PX = 7;

/** Click tolerance around the cloud, so the note is easy to hit. */
export const CALLOUT_HIT_PADDING_PX = 6;

/** Longer than this is a document, not a callout. */
export const CALLOUT_MAX_CHARS = 280;

export const CALLOUT_FONT = `${CALLOUT_FONT_PX}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;

/**
 * The callouts a room carries. Tolerates a room saved before the field
 * existed, and a Firestore map-shaped array.
 *
 * @param {Object} room
 * @returns {Array<Object>}
 */
export const calloutsOf = (room) => {
  const raw = room?.callouts;
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (raw && typeof raw === 'object') return Object.values(raw).filter(Boolean);
  return [];
};

/**
 * A new note, ready to push onto `room.callouts`.
 *
 * @param {Object} params
 * @param {{x: number, z: number}} params.anchor - What the note points at
 * @param {{x: number, z: number}} params.cloud - Where the cloud sits
 * @param {string} params.text
 * @param {number} [params.now] - Injected for deterministic ids in tests
 * @returns {Object}
 */
export const createCallout = ({ anchor, cloud, text, now = Date.now() }) => ({
  id: `callout_${now}`,
  // Room for a second kind later (a detail bubble, an elevation marker)
  // without a second array or a second reader.
  kind: 'note',
  anchor: { x: anchor.x, z: anchor.z },
  cloud: { x: cloud.x, z: cloud.z },
  text: String(text ?? '').slice(0, CALLOUT_MAX_CHARS),
  createdAt: new Date(now).toISOString(),
});

/**
 * Break text into lines that fit `maxWidth`, honouring the newlines the user
 * typed and wrapping what is left on word boundaries.
 *
 * A single word longer than the line is left to overflow rather than being
 * broken mid-word: a long word here is a part number or a dimension, and
 * splitting those makes them wrong.
 *
 * @param {string} text
 * @param {(s: string) => number} measure - Width of a string, pixels
 * @param {number} [maxWidth]
 * @returns {string[]}
 */
export const wrapCalloutText = (
  text,
  measure,
  maxWidth = CALLOUT_MAX_WIDTH_PX
) => {
  const source = String(text ?? '');
  if (!source.trim()) return [];

  const lines = [];
  for (const paragraph of source.split('\n')) {
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (!words.length) {
      lines.push('');
      continue;
    }
    let current = words[0];
    for (const word of words.slice(1)) {
      const candidate = `${current} ${word}`;
      if (measure(candidate) <= maxWidth) current = candidate;
      else {
        lines.push(current);
        current = word;
      }
    }
    lines.push(current);
  }
  return lines;
};

/**
 * The cloud's box for a set of lines — the RECTANGLE the scallops are hung on,
 * not the scalloped outline itself, which bulges `CALLOUT_SCALLOP_PX` beyond
 * it on every side.
 *
 * @param {string[]} lines
 * @param {(s: string) => number} measure - Width of a string, pixels
 * @returns {{width: number, height: number}} Pixels
 */
export const calloutBoxSize = (lines, measure) => {
  const widest = lines.reduce((max, line) => Math.max(max, measure(line)), 0);
  return {
    width: Math.max(CALLOUT_MIN_WIDTH_PX, widest + CALLOUT_PADDING_PX * 2),
    height:
      Math.max(1, lines.length) * CALLOUT_LINE_HEIGHT_PX +
      CALLOUT_PADDING_PX * 2,
  };
};

/**
 * The cloud's box centred on a screen point.
 *
 * @param {{x: number, y: number}} center - Screen pixels
 * @param {{width: number, height: number}} size
 * @returns {{left: number, top: number, right: number, bottom: number, width: number, height: number, cx: number, cy: number}}
 */
export const calloutRect = (center, size) => ({
  left: center.x - size.width / 2,
  top: center.y - size.height / 2,
  right: center.x + size.width / 2,
  bottom: center.y + size.height / 2,
  width: size.width,
  height: size.height,
  cx: center.x,
  cy: center.y,
});

/**
 * Where the leader leaves the cloud: the point on the cloud's outline, along
 * the line from its centre to the anchor.
 *
 * The line is clipped against the box grown by the scallop radius, so the
 * leader starts at the scalloped edge rather than under it. An anchor inside
 * the cloud yields the centre — degenerate, but a leader is meaningless then
 * anyway and the caller draws nothing.
 *
 * @param {Object} rect - From `calloutRect`
 * @param {{x: number, y: number}} target - Anchor, screen pixels
 * @param {number} [grow] - Outset, pixels
 * @returns {{x: number, y: number}}
 */
export const leaderExitPoint = (rect, target, grow = CALLOUT_SCALLOP_PX) => {
  const dx = target.x - rect.cx;
  const dy = target.y - rect.cy;
  const halfW = rect.width / 2 + grow;
  const halfH = rect.height / 2 + grow;
  if (dx === 0 && dy === 0) return { x: rect.cx, y: rect.cy };

  // How far along the ray each bounding plane is crossed; the nearer crossing
  // is the side the ray actually leaves through.
  const tx = dx === 0 ? Infinity : halfW / Math.abs(dx);
  const ty = dy === 0 ? Infinity : halfH / Math.abs(dy);
  const t = Math.min(tx, ty, 1);
  return { x: rect.cx + dx * t, y: rect.cy + dy * t };
};

/**
 * Is a screen point on this callout's cloud?
 *
 * Measured against the box the overlay actually drew, grown by the scallop
 * radius and a click tolerance — the same "aim at the glyph, not the data"
 * rule the electrical symbols follow.
 *
 * @param {{x: number, y: number}} point - Screen pixels
 * @param {Object} rect - From `calloutRect`
 * @returns {boolean}
 */
export const isPointOnCallout = (point, rect) => {
  const pad = CALLOUT_SCALLOP_PX + CALLOUT_HIT_PADDING_PX;
  return (
    point.x >= rect.left - pad &&
    point.x <= rect.right + pad &&
    point.y >= rect.top - pad &&
    point.y <= rect.bottom + pad
  );
};

/**
 * The callout under a screen point, topmost first.
 *
 * `hits` is what the overlay published for the frame the user clicked on, so
 * a note is only clickable where it is actually drawn.
 *
 * @param {Array<{id: string, roomId: string, rect: Object}>} hits
 * @param {{x: number, y: number}} point - Screen pixels
 * @returns {Object|null}
 */
export const calloutAtPoint = (hits, point) => {
  if (!Array.isArray(hits) || !point) return null;
  for (let i = hits.length - 1; i >= 0; i -= 1) {
    if (hits[i]?.rect && isPointOnCallout(point, hits[i].rect)) return hits[i];
  }
  return null;
};
